import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabase';
import BookingModal from './common/BookingModal';

const { FiCalendar, FiUsers, FiHeart, FiGift, FiStar, FiCheck, FiExternalLink } = FiIcons;

const Services = () => {
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Default icons mapping
  const iconMap = {
    weekly: FiCalendar,
    team: FiUsers,
    bridal: FiHeart,
    birthday: FiGift,
    surprise: FiStar,
    private: FiHeart
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('service_links_1740486000000')
        .select('*')
        .order('service_key');
      if (data && data.length > 0) {
        setServicesData(data);
      }
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  if (loading) return null;

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-rose-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
        <h2 className="text-5xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent mb-6">
          Our Services
        </h2>
        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
          Choose the perfect creative journey at Canvas & Clarity.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 md:grid-cols-2 gap-10">
        {servicesData.map((service, idx) => (
          <motion.div
            key={service.service_key}
            whileHover={{ y: -10 }}
            className={`bg-white rounded-[2.5rem] p-10 shadow-xl border border-gray-100 flex flex-col ${service.is_popular ? 'ring-2 ring-rose-500 relative' : ''}`}
          >
            {service.is_popular && (
              <span className="absolute top-0 right-0 bg-rose-500 text-white px-6 py-1.5 rounded-bl-[1.5rem] rounded-tr-[2.5rem] text-sm font-bold italic">
                Most Popular
              </span>
            )}
            <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-8 mx-auto">
              <SafeIcon icon={iconMap[service.service_key] || FiStar} className="w-8 h-8 text-rose-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              {service.title || service.service_key}
            </h3>
            <p className="text-gray-500 mb-8 flex-grow">
              {service.description}
            </p>
            <div className="space-y-4 mb-10 text-left">
              {(service.features || []).map((f, i) => (
                <div key={i} className="flex items-center space-x-3 text-sm text-gray-600">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-8">
              <div className="flex justify-between items-center mb-6">
                <span className="text-gray-400 font-bold uppercase text-xs tracking-widest">
                  {service.duration}
                </span>
                <span className="text-2xl font-black text-gray-800">
                  {service.price}
                </span>
              </div>
              <button 
                onClick={() => handleBookingClick(service)} 
                className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 transition-all ${service.is_popular ? 'bg-rose-500 text-white shadow-lg hover:bg-rose-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <span>Book Now</span>
                <SafeIcon icon={FiCalendar} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <BookingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        serviceTitle={selectedService?.title || ""}
      />
    </section>
  );
};

export default Services;